import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type { CreativeKind } from '../models/analysis.types'
import {
  decodeLabelEditCommitResult,
  decodeLabelEditPreview,
  decodeLabelOverrides,
} from '../models/labelEdit.codec'
import type {
  CreativeLabelOverride,
  LabelEditCommitResult,
  LabelEditInput,
  LabelEditPreview,
} from '../models/labelEdit.types'

import type { CreativeAiScope } from './CreativeAiApi'

import { ApiError, appHttp, decodeDto, unwrap } from '@/shared/http'

function requestConfig(signal?: AbortSignal): AxiosRequestConfig {
  return {
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
    ...(signal ? { signal } : {}),
  }
}

function base(scope: CreativeAiScope, kind: CreativeKind): string {
  return `/accounts/${encodeURIComponent(String(scope.accountId))}/workspaces/${encodeURIComponent(
    String(scope.workspaceId),
  )}/metadata/creative-ai/creatives/${kind}`
}

async function featureRequest<T>(request: Promise<T>): Promise<T> {
  try {
    return await request
  } catch (cause) {
    throw ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  }
}

export class CreativeLabelEditApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  async previewLabelEdits(
    scope: CreativeAiScope,
    kind: CreativeKind,
    creativeIds: readonly string[],
    signal?: AbortSignal,
  ): Promise<LabelEditPreview> {
    const response = await featureRequest(
      this.http.post(
        `${base(scope, kind)}/labels/bulk/preview/`,
        { creative_ids: [...creativeIds] },
        requestConfig(signal),
      ),
    )
    return decodeDto(unwrap<unknown>(response), decodeLabelEditPreview)
  }

  async commitLabelEdits(
    scope: CreativeAiScope,
    kind: CreativeKind,
    edits: readonly LabelEditInput[],
  ): Promise<LabelEditCommitResult> {
    const response = await featureRequest(
      this.http.patch(`${base(scope, kind)}/labels/bulk/`, { edits: [...edits] }, requestConfig()),
    )
    return decodeDto(unwrap<unknown>(response), decodeLabelEditCommitResult)
  }

  async listLabelOverrides(
    scope: CreativeAiScope,
    kind: CreativeKind,
    signal?: AbortSignal,
  ): Promise<CreativeLabelOverride[]> {
    const response = await featureRequest(
      this.http.get(`${base(scope, kind)}/labels/overrides/`, requestConfig(signal)),
    )
    return decodeDto(unwrap<unknown>(response), decodeLabelOverrides)
  }
}

export const creativeLabelEditApi = new CreativeLabelEditApi()
